import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, Phone, MessageCircle, Mail, History, Calendar, X, FileText, ShoppingBag, Route, Pencil, StickyNote, Car, LifeBuoy, Merge, ClipboardCheck } from 'lucide-react';
import api, { API } from '../lib/api';
import { formatCurrency, formatDate } from '../mock';
import { toast } from '../hooks/use-toast';
import { useDeepLink } from '../hooks/useDeepLink';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DealDetailDialog, DEAL_STAGES } from './FiDeals';
import { CustomerGarage, CustomerPostSale, MergeCustomerDialog } from '../components/CustomerGarage';
import { CustomerInspections } from '../components/CustomerInspections';

const origemStyle = {
  'Instagram': 'text-pink-400 bg-pink-400/10', 'Site': 'text-sky-400 bg-sky-400/10',
  'WhatsApp': 'text-emerald-400 bg-emerald-400/10', 'Indicação': 'text-purple-400 bg-purple-400/10',
  'Facebook': 'text-blue-400 bg-blue-400/10', 'Loja física': 'text-orange-400 bg-orange-400/10',
  'Telefone': 'text-teal-400 bg-teal-400/10', 'Capital One': 'text-red-400 bg-red-400/10',
};

const deptStyle = {
  'BDC/CRM': 'text-sky-400 bg-sky-400/10', 'F&I': 'text-violet-400 bg-violet-400/10',
  'SERVICE': 'text-blue-400 bg-blue-400/10', 'DELIVERY': 'text-emerald-400 bg-emerald-400/10',
  'NOTE': 'text-amber-400 bg-amber-400/10', 'EDIÇÃO': 'text-rose-400 bg-rose-400/10',
};

const JOURNEY_STEPS = ['BDC', 'CRM', 'Venda', 'F&I', 'Service', 'Entregue'];

const stageIdx = (j) => {
  const s = j.stage;
  if (s === 'DELIVERED') return 5;
  if (s === 'SERVICE' || s === 'READY_FOR_DELIVERY') return 4;
  if (['CONTRACT_SIGNED', 'WAITING_WARRANTY', 'WAITING_INSURANCE', 'WAITING_TITLE', 'WAITING_REGISTRATION', 'REGISTRATION_COMPLETE'].includes(s)) return 3;
  if (s === 'CRM') return 1;
  if (s === 'BDC') return 0;
  return -1;
};

const fmtDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const SectionTitle = ({ icon: Icon, children }) => (
  <h4 className="flex items-center gap-2 text-xs tracking-[0.2em] text-muted-foreground font-semibold mb-3"><Icon className="w-3.5 h-3.5" /> {children}</h4>
);

const JourneyStepper = ({ idx }) => (
  <div className="flex items-center gap-1 flex-wrap" data-testid="customer-journey-stepper">
    {JOURNEY_STEPS.map((s, i) => (
      <React.Fragment key={s}>
        {i > 0 && <span className={`h-px w-3 shrink-0 ${i <= idx ? 'bg-primary' : 'bg-muted/60'}`} />}
        <span className={`text-[10px] px-2 py-1 rounded font-semibold whitespace-nowrap ${i === idx ? 'bg-primary text-white' : i < idx ? 'text-emerald-400 bg-emerald-400/10' : 'text-muted-foreground/60 bg-muted/40'}`}>{s}</span>
      </React.Fragment>
    ))}
  </div>
);

const Clientes = () => {
  const [list, setList] = useState([]);
  const [query, setQuery] = useState('');
  const [journey, setJourney] = useState(null);
  const [dealId, setDealId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const loadList = async () => { try { const r = await api.get('/customers'); setList(r.data); } catch (e) {} };
  useEffect(() => { loadList(); }, []);

  const openEdit = () => {
    if (!journey) return;
    const c = journey.customer;
    setEditForm({ ...c });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm?.nome) { toast({ title: 'Nome é obrigatório' }); return; }
    setEditSaving(true);
    try {
      await api.put(`/customers/${editForm.id}`, editForm);
      toast({ title: 'Cliente atualizado', description: 'Alterações registradas no log do cliente.' });
      setEditOpen(false);
      await Promise.all([loadList(), openDetail(editForm.id)]);
    } catch (e) { toast({ title: 'Erro ao salvar' }); } finally { setEditSaving(false); }
  };

  const saveNote = async () => {
    if (!noteText.trim()) { toast({ title: 'Escreva a nota' }); return; }
    setNoteSaving(true);
    try {
      const { data } = await api.post(`/customers/${journey.customer.id}/notes`, { note: noteText });
      toast({ title: 'Nota adicionada', description: data.leads_tagged ? 'Visível também na timeline do lead.' : undefined });
      setNoteText(''); setNoteOpen(false);
      await openDetail(journey.customer.id);
    } catch (e) { toast({ title: 'Erro ao salvar nota', description: e.response?.data?.detail || '' }); } finally { setNoteSaving(false); }
  };

  const openDetail = async (id) => {
    try { const r = await api.get(`/customers/${id}/journey`); setJourney(r.data); } catch (e) {}
  };

  useDeepLink('c', openDetail);

  const filtered = list.filter((c) => {
    const q = query.toLowerCase();
    return !q || `${c.nome} ${c.telefone} ${c.email} ${(c.vehicles || []).map((v) => `${v.vin} ${v.desc}`).join(' ')}`.toLowerCase().includes(q);
  });

  const initials = (n) => (n || '?').split(' ').slice(0, 2).map((x) => x[0]).join('').toUpperCase();
  const j = journey;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">{list.length} clientes na base consolidada</p>
        <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg px-3 py-2 w-64">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input data-testid="customer-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nome, telefone, e-mail..." className="bg-transparent outline-none text-sm w-full placeholder:text-muted-foreground/60" />
        </div>
      </div>

      <div className="rounded-xl border border-border/70 bg-card p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground text-xs tracking-wider border-b border-border/70">
              <th className="pb-3 font-semibold">CLIENTE</th>
              <th className="pb-3 font-semibold hidden lg:table-cell">CONTATO</th>
              <th className="pb-3 font-semibold">ORIGEM</th>
              <th className="pb-3 font-semibold hidden sm:table-cell">VENDEDOR</th>
              <th className="pb-3 font-semibold text-center">ATEND.</th>
              <th className="pb-3 font-semibold hidden md:table-cell">ÚLTIMO</th>
              <th className="pb-3 font-semibold text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-border/70 hover:bg-muted/40 transition-colors cursor-pointer" onClick={() => openDetail(c.id)}>
                <td className="py-3.5">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">{initials(c.nome)}</span>
                    <div><p className="font-medium">{c.nome}</p><p className="text-[11px] text-muted-foreground">{c.email}</p></div>
                  </div>
                </td>
                <td className="py-3.5 hidden lg:table-cell text-foreground/65">{c.telefone}</td>
                <td className="py-3.5"><span className={`text-[11px] px-2 py-1 rounded-full font-medium ${origemStyle[c.origem] || 'text-muted-foreground bg-muted/40'}`}>{c.origem}</span></td>
                <td className="py-3.5 text-foreground/75 hidden sm:table-cell">{c.vendedor || '—'}</td>
                <td className="py-3.5 text-center"><span className="text-xs font-semibold bg-muted/50 px-2 py-1 rounded-full">{c.totalAtendimentos}</span></td>
                <td className="py-3.5 text-muted-foreground hidden md:table-cell">{formatDate(c.ultimoAtendimento)}</td>
                <td className="py-3.5 text-right"><History className="w-4 h-4 text-muted-foreground/60 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center py-14 text-muted-foreground">Nenhum cliente encontrado.</div>}
      </div>

      {/* Customer Card — vida contínua */}
      {j && createPortal(
        <div className="fixed inset-0 z-[90] flex justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => setJourney(null)} />
          <div className="relative z-10 w-full max-w-2xl h-full bg-card border-l border-border overflow-y-auto p-6 animate-fade-up" data-testid="customer-card">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-bold">{initials(j.customer.nome)}</span>
                <div>
                  <h3 className="font-display font-black text-lg" data-testid="customer-card-name">{j.customer.nome}</h3>
                  <p className="text-xs text-muted-foreground">{j.customer.origem} · desde {formatDate(j.customer.criadoEm)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="customer-note-btn" onClick={() => setNoteOpen(true)} className="flex items-center gap-1.5 text-xs font-semibold border border-border rounded-lg px-3 py-2 text-foreground/70 hover:text-foreground hover:border-primary/50 transition-colors"><StickyNote className="w-3.5 h-3.5" /> Adicionar nota</button>
                <button data-testid="customer-merge-btn" onClick={() => setMergeOpen(true)} className="flex items-center gap-1.5 text-xs font-semibold border border-border rounded-lg px-3 py-2 text-foreground/70 hover:text-foreground hover:border-primary/50 transition-colors"><Merge className="w-3.5 h-3.5" /> Mesclar</button>
                <button data-testid="customer-edit-btn" onClick={openEdit} className="flex items-center gap-1.5 text-xs font-semibold border border-border rounded-lg px-3 py-2 text-foreground/70 hover:text-foreground hover:border-primary/50 transition-colors"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                <button data-testid="customer-card-close" onClick={() => setJourney(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4 text-sm">
              {j.customer.telefone && <a href={`tel:${j.customer.telefone}`} className="flex items-center gap-1.5 text-foreground/65 hover:text-foreground"><Phone className="w-4 h-4" />{j.customer.telefone}</a>}
              {j.customer.whatsapp && <a href={`https://wa.me/${j.customer.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300"><MessageCircle className="w-4 h-4" /></a>}
              {j.customer.email && <a href={`mailto:${j.customer.email}`} className="text-foreground/65 hover:text-foreground"><Mail className="w-4 h-4" /></a>}
            </div>

            <div className="mt-5 rounded-lg border border-border/70 bg-popover p-4">
              <SectionTitle icon={Route}>JORNADA DO CLIENTE</SectionTitle>
              <JourneyStepper idx={stageIdx(j)} />
            </div>

            <div className="mt-6">
              <SectionTitle icon={ShoppingBag}>HISTÓRICO DE COMPRAS ({j.deals.length})</SectionTitle>
              <div className="space-y-2.5">
                {j.deals.map((d) => {
                  const m = DEAL_STAGES[d.status] || { label: d.status, cls: '' };
                  return (
                    <button key={d.id} data-testid={`customer-deal-${d.id}`} onClick={() => setDealId(d.id)} className="w-full text-left rounded-lg border border-border/70 bg-popover p-3.5 hover:border-primary/50 transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm">{d.vehicle_desc}</p>
                        <span className={`text-[10px] px-2 py-1 rounded-full font-semibold whitespace-nowrap ${m.cls}`}>{m.label}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">{d.id} · {formatDate(d.sale_date)} · {d.salesperson_name} · {formatCurrency(d.sale_price)} · {d.finance_type}</p>
                    </button>
                  );
                })}
                {j.deals.length === 0 && <p className="text-sm text-muted-foreground/70">Nenhuma compra ainda.</p>}
              </div>
            </div>

            <div className="mt-6">
              <SectionTitle icon={Car}>GARAGEM — VEÍCULOS DO CLIENTE ({(j.vehicles || []).length})</SectionTitle>
              <CustomerGarage vehicles={j.vehicles || []} cases={j.post_sale || []} />
            </div>

            <div className="mt-6">
              <SectionTitle icon={ClipboardCheck}>INSPEÇÕES DE ENTREGA</SectionTitle>
              <CustomerInspections deals={j.deals || []} />
            </div>

            <div className="mt-6">
              <SectionTitle icon={LifeBuoy}>PÓS-VENDA ({(j.post_sale || []).length})</SectionTitle>
              <CustomerPostSale cases={j.post_sale || []} />
            </div>

            <div className="mt-6">
              <SectionTitle icon={FileText}>DOCUMENTOS & FOTOS ({j.documents.length})</SectionTitle>
              <div className="space-y-1.5">
                {j.documents.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 text-[12px] bg-muted/40 border border-border/70 rounded px-2.5 py-2" data-testid={`customer-doc-${f.id}`}>
                    <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                    <a href={`${API}/deals/files/${f.path}`} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">{f.label}{f.original_filename ? ` — ${f.original_filename}` : ''}</a>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${f.category === 'F&I' ? 'text-violet-400 bg-violet-400/10' : 'text-blue-400 bg-blue-400/10'}`}>{f.category}</span>
                    <span className="ml-auto text-muted-foreground/60 text-[10px] whitespace-nowrap">{f.uploaded_by} · {f.deal_id}</span>
                  </div>
                ))}
                {j.documents.length === 0 && <p className="text-sm text-muted-foreground/70">Nenhum documento anexado.</p>}
              </div>
            </div>

            <div className="mt-6">
              <SectionTitle icon={History}>LINHA DO TEMPO COMPLETA</SectionTitle>
              <div className="space-y-0 max-h-[380px] overflow-y-auto pr-1" data-testid="customer-timeline">
                {j.events.slice(0, 100).map((ev, i) => (
                  <div key={i} className="flex gap-3 relative pb-3.5 last:pb-0">
                    <div className="flex flex-col items-center"><span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" /><span className="w-px flex-1 bg-muted/50" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${deptStyle[ev.dept] || 'text-muted-foreground bg-muted/40'}`}>{ev.dept}</span>
                        <p className="text-[13px] font-medium">{ev.action}</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground/70">{fmtDT(ev.ts)} · {ev.user}{ev.deal_id ? ` · ${ev.deal_id}` : ''}</p>
                      {ev.note && <p className="text-[11px] text-foreground/60 mt-0.5 bg-muted/40 border border-border/70 rounded px-2 py-1">{ev.note}</p>}
                    </div>
                  </div>
                ))}
                {j.events.length === 0 && <p className="text-sm text-muted-foreground/70">Sem atividades registradas.</p>}
              </div>
            </div>

            <div className="mt-6">
              <SectionTitle icon={Calendar}>AGENDAMENTOS</SectionTitle>
              <div className="space-y-2">
                {j.appointments.map((ap) => (
                  <div key={ap.id} className="flex items-center justify-between border border-border/70 rounded-lg p-3">
                    <div><p className="text-sm font-medium">{ap.titulo}</p><p className="text-[11px] text-muted-foreground">{ap.tipo} · {formatDate(ap.data)} {ap.hora}</p></div>
                    <span className="text-[11px] text-muted-foreground">{ap.status}</span>
                  </div>
                ))}
                {j.appointments.length === 0 && <p className="text-sm text-muted-foreground/70">Nenhum agendamento.</p>}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {j && <MergeCustomerDialog source={j.customer} open={mergeOpen} onClose={() => setMergeOpen(false)} onMerged={(id) => { loadList(); openDetail(id); }} />}

      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Nota do cliente</DialogTitle></DialogHeader>
          <p className="text-[11.5px] text-muted-foreground -mt-1">A nota entra na ficha do cliente e na timeline dos leads dele.</p>
          <textarea data-testid="customer-note-input" rows={4} value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="Ex: cliente pediu para ligar depois das 18h" className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary resize-y" />
          <DialogFooter>
            <button data-testid="customer-note-cancel" onClick={() => setNoteOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-semibold border border-border text-foreground/70 hover:text-foreground transition-colors">Cancelar</button>
            <button data-testid="customer-note-save" onClick={saveNote} disabled={noteSaving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{noteSaving ? 'Salvando...' : 'Salvar nota'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Editar cliente</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Nome</label><input data-testid="edit-customer-nome" className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary" value={editForm.nome || ''} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Telefone</label><input data-testid="edit-customer-telefone" className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary" value={editForm.telefone || ''} onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">WhatsApp</label><input data-testid="edit-customer-whatsapp" className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary" value={editForm.whatsapp || ''} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">E-mail</label><input data-testid="edit-customer-email" className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Origem</label><select data-testid="edit-customer-origem" className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary" value={editForm.origem || ''} onChange={(e) => setEditForm({ ...editForm, origem: e.target.value })}>{['Loja física', 'Instagram', 'Facebook', 'Site', 'WhatsApp', 'Indicação', 'Telefone', 'CarGurus', 'Capital One', 'Outro'].map((o) => <option key={o} className="bg-popover">{o}</option>)}</select></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Vendedor</label><input data-testid="edit-customer-vendedor" className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary" value={editForm.vendedor || ''} onChange={(e) => setEditForm({ ...editForm, vendedor: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Observações</label><textarea data-testid="edit-customer-observacoes" rows={2} className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary resize-none" value={editForm.observacoes || ''} onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
            <button data-testid="edit-customer-save" onClick={saveEdit} disabled={editSaving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{editSaving ? 'Salvando...' : 'Salvar alterações'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DealDetailDialog dealId={dealId} open={!!dealId} onClose={() => setDealId(null)} onChanged={() => j && openDetail(j.customer.id)} />
    </div>
  );
};

export default Clientes;
